//changed due to package upgrade
import React from 'react';
import { useAuthUser } from '@projectK/hooks/AuthHooks';
import { Formik, Form } from 'formik';
import * as yup from 'yup';
import PropTypes from 'prop-types';
import {
  alpha,
  Box,
  Button,
  FormControl,
  FormHelperText,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import AppGridContainer from '@projectK/components/AppGridContainer';
import Grid from '@mui/material/Grid';
import IntlMessages from '@projectK/helpers/IntlMessages';
import { useInfoViewActionsContext } from '@projectK/context/InfoViewContextProvider';
import jwtAxios from '@projectK/services/auth/JWT';
import { Fonts } from '@projectK/constants/AppEnums';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import AppInfoView from '@projectK/components/AppInfoView';
import CompanyTheme from '@projectK/constants/CompanyTheme';
import AppCard from '@projectK/components/AppCard';
import { format } from 'date-fns';

const validationSchema = yup.object({
  date_of_birth: yup
    .date()
    .typeError('Please enter a valid date.')
    .nullable()
    .optional()
    .test({
      name: 'futureDate',
      exclusive: false,
      message: 'Please enter a valid date.',
      test: function (value) {
        const today = new Date();
        return !value || new Date(value) < today;
      },
    }),
  legal_first_name: yup.string().nullable(),
  legal_middle_name: yup.string().nullable(),
  legal_last_name: yup.string().nullable(),
  gender: yup.string().nullable().optional(),
  address: yup
    .string()
    .max(255, 'Address must be at most 255 characters.')
    .nullable(),
  state: yup
    .string()
    .max(50, 'State must be at most 50 characters.')
    .matches(/^[A-Za-z]+(?:\s[A-Za-z]+)*$/, 'Please enter a valid state. ')
    .nullable(),
  city: yup
    .string()
    .max(50, 'City must be at most 50 characters.')
    .matches(/^[A-Za-z]+(?:[\s-][A-Za-z]+)*$/, 'Please enter a valid city.')
    .required('City is required.'),
  zip_code: yup
    .string()
    .matches(/^\d{5,6}$/, 'Zip code is not valid.')
    .nullable(),
});
const Information = ({ fetchSettings, onClose, settings }) => {
  const companyTheme = CompanyTheme();
  const infoViewActionsContext = useInfoViewActionsContext();
  const { user } = useAuthUser();

  return (
    <AppCard>
      <Box>
        <Formik
          enableReinitialize={true}
          initialValues={{
            date_of_birth: settings?.date_of_birth
              ? `${settings.date_of_birth.substring(0, 10)}T12:01:00`
              : '',
            legal_first_name: settings?.legal_first_name || '',
            legal_middle_name: settings?.legal_middle_name || '',
            legal_last_name: settings?.legal_last_name || '',
            gender: settings?.gender || '',
            address: settings?.address || '',
            city: settings?.city || '',
            state: settings?.state || '',
            zip_code: settings?.zip_code || '',
          }}
          validationSchema={validationSchema}
          onSubmit={(values, { setSubmitting }) => {
            values.date_of_birth = values.date_of_birth
              ? format(new Date(values.date_of_birth), 'yyyy-MM-dd')
              : null;
            const reqData = {
              zip_code: values.zip_code ? values.zip_code + '' : null,
              date_of_birth: values.date_of_birth,
              legal_first_name:
                values.legal_first_name === '' ? null : values.legal_first_name,
              legal_middle_name:
                values.legal_middle_name === ''
                  ? null
                  : values.legal_middle_name,
              legal_last_name:
                values.legal_last_name === '' ? null : values.legal_last_name,
              gender: values.gender === '' ? null : values.gender,
              address: values.address === '' ? null : values.address,
              city: values.city === '' ? null : values.city,
              state: values.state === '' ? null : values.state,
            };
            setSubmitting(true);
            jwtAxios
              .put(`/users/${user.id}`, reqData)
              .then((response) => {
                setSubmitting(false);
                fetchSettings();
                infoViewActionsContext.showMessage(
                  'Information Updated Successfully',
                );
              })
              .catch((error) => {
                setSubmitting(false);
                infoViewActionsContext.fetchError(error.message);
              });
          }}
        >
          {({
            values,
            setFieldValue,
            handleChange,
            resetForm,
            touched,
            errors,
            setErrors,
          }) => {
            return (
              <Form noValidate autoComplete='off'>
                <Typography
                  component='h3'
                  sx={{
                    fontSize: 16,
                    fontWeight: Fonts.BOLD,
                    color: companyTheme
                      ? companyTheme.primary.main
                      : 'text.primary',
                  }}
                >
                  <IntlMessages id='common.ProfileFieldData' />
                </Typography>
                <Grid size={{ xs: 12, md: 9 }}>
                  <AppGridContainer sx={{ mt: 2 }}>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        name='legal_first_name'
                        value={values.legal_first_name}
                        onChange={(event) => {
                          setFieldValue(
                            'legal_first_name',
                            event.target.value === ''
                              ? null
                              : event.target.value,
                          );
                          touched.legal_first_name = true;
                        }}
                        error={
                          touched.legal_first_name &&
                          Boolean(errors.legal_first_name)
                        }
                        helperText={
                          touched.legal_first_name && errors.legal_first_name
                        }
                        sx={{
                          width: '100%',
                        }}
                        label='Legal First Name'
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        name='legal_middle_name'
                        value={values.legal_middle_name}
                        onChange={(event) => {
                          setFieldValue(
                            'legal_middle_name',
                            event.target.value === ''
                              ? null
                              : event.target.value,
                          );
                          touched.legal_middle_name = true;
                        }}
                        error={
                          touched.legal_middle_name &&
                          Boolean(errors.legal_middle_name)
                        }
                        helperText={
                          touched.legal_middle_name && errors.legal_middle_name
                        }
                        variant='outlined'
                        sx={{
                          width: '100%',
                        }}
                        label='Legal Middle Name'
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        name='legal_last_name'
                        value={values.legal_last_name}
                        onChange={(event) => {
                          setFieldValue(
                            'legal_last_name',
                            event.target.value === ''
                              ? null
                              : event.target.value,
                          );
                          touched.legal_last_name = true;
                        }}
                        error={
                          touched.legal_last_name &&
                          Boolean(errors.legal_last_name)
                        }
                        helperText={
                          touched.legal_last_name && errors.legal_last_name
                        }
                        variant='outlined'
                        sx={{
                          width: '100%',
                        }}
                        label='Legal Last Name'
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <FormControl sx={{ width: '100%', borderRadius: 50 }}>
                        <TextField
                          sx={{
                            width: '100%',
                          }}
                          select
                          name='gender'
                          value={values.gender}
                          label='Gender'
                          error={touched.gender && Boolean(errors.gender)}
                          helperText={touched.gender && errors.gender}
                          onChange={(event) => {
                            setFieldValue('gender', event.target.value); // Update the form field value
                          }}
                        >
                          <MenuItem value='male'>Male</MenuItem>
                          <MenuItem value='female'>Female</MenuItem>
                        </TextField>
                      </FormControl>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DatePicker
                          disableFuture
                          openTo='year'
                          views={['year', 'month', 'day']}
                          sx={{
                            width: '100%',
                          }}
                          label='Date Of Birth'
                          value={
                            values.date_of_birth
                              ? new Date(values.date_of_birth)
                              : null
                          }
                          onChange={(newValue) => {
                            if (newValue) {
                              setFieldValue(
                                'date_of_birth',
                                new Date(newValue).toUTCString(),
                              );
                              touched.date_of_birth = true;
                            } else {
                              setFieldValue('date_of_birth', null);
                            }
                          }}
                          name='date_of_birth'
                          onError={(newError) =>
                            setErrors({
                              date_of_birth: newError,
                            })
                          }
                        />
                        <FormHelperText
                          style={{
                            color: '#d32f2f',
                            marginLeft: '15px',
                          }}
                        >
                          {touched.date_of_birth && errors.date_of_birth}
                        </FormHelperText>
                      </LocalizationProvider>
                    </Grid>
                    <Grid size={{ xs: 12, md: 12 }}>
                      <TextField
                        name='address'
                        value={values.address}
                        variant='outlined'
                        onChange={(event) => {
                          setFieldValue(
                            'address',
                            event.target.value === ''
                              ? null
                              : event.target.value,
                          );
                          touched.address = true;
                        }}
                        error={touched.address && Boolean(errors.address)}
                        helperText={touched.address && errors.address}
                        sx={{
                          width: '100%',
                        }}
                        label='Address'
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        name='state'
                        value={values.state}
                        onChange={(event) => {
                          setFieldValue(
                            'state',
                            event.target.value === ''
                              ? null
                              : event.target.value,
                          );
                          touched.state = true;
                        }}
                        error={touched.state && Boolean(errors.state)}
                        helperText={touched.state && errors.state}
                        variant='outlined'
                        sx={{
                          width: '100%',
                        }}
                        label='State'
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        name='city'
                        value={values.city}
                        onChange={(event) => {
                          setFieldValue(
                            'city',
                            event.target.value === ''
                              ? null
                              : event.target.value,
                          );
                          touched.city = true;
                        }}
                        error={touched.city && Boolean(errors.city)}
                        helperText={touched.city && errors.city}
                        variant='outlined'
                        sx={{
                          width: '100%',
                        }}
                        label='City'
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        name='zip_code'
                        value={values.zip_code}
                        onChange={(event) => {
                          setFieldValue(
                            'zip_code',
                            event.target.value === ''
                              ? null
                              : event.target.value,
                          );
                          touched.zip_code = true;
                        }}
                        error={touched.zip_code && Boolean(errors.zip_code)}
                        helperText={touched.zip_code && errors.zip_code}
                        variant='outlined'
                        sx={{
                          width: '100%',
                        }}
                        label='Zip Code'
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 12 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        <Button
                          sx={{
                            position: 'relative',
                            minWidth: 100,
                            backgroundColor: companyTheme
                              ? companyTheme.primary.main
                              : '#2196f3',
                            '&:hover': {
                              backgroundColor: companyTheme
                                ? alpha(companyTheme.primary.main, 0.8) // Adjust the alpha value as needed
                                : alpha('#2196f3', 0.8),
                            },
                          }}
                          variant='contained'
                          type='submit'
                        >
                          Save
                        </Button>

                        <Button
                          sx={{
                            position: 'relative',
                            minWidth: 100,
                            ml: 2.5,
                            borderColor: companyTheme
                              ? companyTheme.primary.main
                              : '#2196f3',
                            '&:hover': {
                              borderColor: companyTheme
                                ? alpha(companyTheme.primary.main, 0.8)
                                : alpha('#2196f3', 0.8),
                            },
                            color: companyTheme
                              ? companyTheme.primary.main
                              : '#2196f3',
                          }}
                          variant='outlined'
                          onClick={(e) => {
                            e.preventDefault();
                            // Reset the form back to its initial state
                            resetForm();
                          }}
                        >
                          Cancel
                        </Button>
                      </Box>
                    </Grid>
                  </AppGridContainer>
                </Grid>
              </Form>
            );
          }}
        </Formik>
      </Box>
      <AppInfoView />
    </AppCard>
  );
};

export default Information;

Information.propTypes = {
  fetchSettings: PropTypes.func,
  onClose: PropTypes.func,
  settings: PropTypes.object,
};
